// Centralized apartment data for Kenneth Nathanael Lukman LLC
// Fictional premium property names. Exact addresses are intentionally
// withheld and shared only after inquiry/booking confirmation.

export const apartments = [
  {
    id: "heart-of-soho-1br",
    name: "The Beaumont — Modern 1BR Retreat",
    short_name: "The Beaumont",
    area: "Soho",
    short_description:
      "An intimate Soho hideaway moments from West End theatres, jazz at Ronnie Scott's, and the city's most celebrated dining.",
    description:
      "Tucked into the creative pulse of Soho, this elegantly appointed one-bedroom apartment is a refined retreat for discerning travellers. Designed with understated luxury, the open-plan living space opens onto a softly lit lounge with a velvet sofa bed, while the bedroom is dressed in linens chosen for quiet comfort. A spa-style bathroom with both bath and rainfall shower invites you to unwind after an evening at the theatre. Step out of the door and you are seconds from Ronnie Scott's, the West End stages, and Soho's most coveted restaurants — Mayfair, Covent Garden, and Oxford Street are all within an effortless walk.",
    images: [
      "/apartments/image1.jpeg",
      "/apartments/image2.jpeg",
      "/apartments/image3.jpeg",
      "/apartments/image4.jpeg",
      "/apartments/image5.jpeg",
    ],
    features: {
      bedrooms: 1,
      bathrooms: 1,
      guests: 4,
      wifi: true,
      smart_tv: true,
      ac: false,
      kitchen: true,
      washing_machine: false,
      coffee_machine: true,
      elevator: false,
    },
    nearby_attractions: [
      "West End Theatres",
      "Ronnie Scott's Jazz Club",
      "Soho Square",
      "Chinatown",
      "Oxford Street",
    ],
    transport: "Tottenham Court Road & Leicester Square Tube — a short walk",
    // Approximate neighbourhood centre — not the exact building location
    coordinates: { lat: 51.5137, lng: -0.1339 },
  },
  {
    id: "covent-garden-2br",
    name: "The Hawthorne — 2BR Townhouse Apartment",
    short_name: "The Hawthorne",
    area: "Covent Garden",
    short_description:
      "A spacious two-bedroom residence on the doorstep of Seven Dials, ideal for families or friends exploring central London in style.",
    description:
      "Set within an elegant period building moments from the cobbled streets of Seven Dials, this two-bedroom Covent Garden apartment combines classic London character with thoughtfully curated modern comforts. Two beautifully dressed bedrooms — including an ensuite primary — sleep up to six with a generous double sofa bed in the lounge. Gather around the large dining table for six, prepare meals in the fully equipped kitchen, or unwind in front of a 55-inch Smart TV with Netflix and PS4. A safe, family-friendly building, with the Royal Opera House, the Piazza, and the theatres of Drury Lane all an effortless stroll away.",
    images: [
      "/apartments/image6.jpeg",
      "/apartments/image7.jpeg",
      "/apartments/image8.jpeg",
      "/apartments/image9.jpeg",
      "/apartments/image10.jpeg",
    ],
    features: {
      bedrooms: 2,
      bathrooms: 2,
      guests: 6,
      wifi: true,
      smart_tv: true,
      ac: false,
      kitchen: true,
      washing_machine: false,
      coffee_machine: true,
      elevator: false,
    },
    nearby_attractions: [
      "Covent Garden Piazza",
      "Royal Opera House",
      "Seven Dials",
      "Neal's Yard",
      "Theatre District",
    ],
    transport: "Covent Garden Tube — a short walk",
    coordinates: { lat: 51.5142, lng: -0.124 },
  },
  {
    id: "trafalgar-3br-ac",
    name: "The Wellington — Air-Conditioned 3BR Suite",
    short_name: "The Wellington",
    area: "Westminster",
    short_description:
      "An expansive air-conditioned three-bedroom residence with lift access, steps from Trafalgar Square and Whitehall.",
    description:
      "Our largest London residence, this three-bedroom apartment is designed for groups, families, and extended stays in true comfort. Floor-to-ceiling living spaces are wrapped in soft neutral tones, with full air-conditioning throughout — a rare luxury in central London — and a sun-filled open-plan dining and lounge area perfect for entertaining. Three serene bedrooms sleep up to eight with the addition of a sofa bed, served by two beautifully finished bathrooms. The building offers secure lift access and round-the-clock peace of mind. Just outside, Trafalgar Square, the National Gallery, St James's Park, and Westminster unfold like a private gallery of the capital.",
    images: [
      "/apartments/image11.jpeg",
      "/apartments/image12.jpeg",
      "/apartments/image13.jpeg",
      "/apartments/image14.jpeg",
    ],
    features: {
      bedrooms: 3,
      bathrooms: 2,
      guests: 8,
      wifi: true,
      smart_tv: true,
      ac: true,
      kitchen: true,
      washing_machine: true,
      coffee_machine: false,
      elevator: true,
    },
    nearby_attractions: [
      "Trafalgar Square",
      "National Gallery",
      "St James's Park",
      "Buckingham Palace",
      "Westminster Abbey",
    ],
    transport: "Charing Cross & Piccadilly Circus Tube — a short walk",
    coordinates: { lat: 51.5079, lng: -0.1281 },
  },
  {
    id: "stylish-soho-2br",
    name: "The Carlisle — 2BR Ensuite Residence",
    short_name: "The Carlisle",
    area: "Soho",
    short_description:
      "A boldly contemporary two-bedroom apartment in the beating heart of Soho — perfect for design-led travellers.",
    description:
      "Set on the third floor of a classic Soho building, this two-bedroom apartment is a study in confident contemporary design. Both bedrooms feature their own ensuite bathrooms — a rarity in central London — and the generous living room is anchored by a 75-inch Smart TV, PlayStation 5, and a deep-set sofa that converts to a double bed for friends. Secondary glazing ensures total tranquility despite the buzz outside, while a Nespresso coffee machine and fully equipped kitchen make slow mornings effortless. Step out and you are immersed in Soho's restaurants, galleries, and the West End. Please note the building has stair access only.",
    images: [
      "/apartments/image15.jpeg",
      "/apartments/image16.jpeg",
      "/apartments/image17.jpeg",
    ],
    features: {
      bedrooms: 2,
      bathrooms: 2,
      guests: 6,
      wifi: true,
      smart_tv: true,
      ac: false,
      kitchen: true,
      washing_machine: false,
      coffee_machine: true,
      elevator: false,
    },
    nearby_attractions: [
      "Soho Square",
      "Chinatown",
      "Carnaby Street",
      "Oxford Circus Shopping",
      "West End",
    ],
    transport: "Tottenham Court Road & Leicester Square Tube — a short walk",
    coordinates: { lat: 51.5137, lng: -0.1339 },
  },
  {
    id: "strand-central-2br",
    name: "The Somerset — Central 2BR by the Thames",
    short_name: "The Somerset",
    area: "Strand",
    short_description:
      "A polished two-bedroom apartment on the historic Strand, walking distance to Covent Garden, Trafalgar Square, and the Thames.",
    description:
      "Perched on London's storied Strand, this elegant two-bedroom apartment places you at the crossroads of Covent Garden, Trafalgar Square, and the Thames. The light-filled living space is centred around a 55-inch Smart TV with Netflix and PS4, opening onto a generous dining area perfect for long suppers. Two refined bedrooms — each with its own 50-inch Smart TV — sleep up to six with the addition of a double sofa bed, served by a single modern bathroom with bathtub and shower. The fully equipped kitchen and Nespresso coffee machine invite leisurely mornings, while Covent Garden and Charing Cross Tube stations are a short walk away.",
    images: [
      "/apartments/image18.jpeg",
      "/apartments/image19.jpeg",
      "/apartments/image20.jpeg",
      "/apartments/image21.jpeg",
    ],
    features: {
      bedrooms: 2,
      bathrooms: 1,
      guests: 6,
      wifi: true,
      smart_tv: true,
      ac: false,
      kitchen: true,
      washing_machine: false,
      coffee_machine: true,
      elevator: false,
    },
    nearby_attractions: [
      "Trafalgar Square",
      "Thames Embankment",
      "Covent Garden",
      "Somerset House",
      "Royal Courts of Justice",
    ],
    transport: "Covent Garden & Charing Cross Tube — a short walk",
    coordinates: { lat: 51.5118, lng: -0.117 },
  },
  {
    id: "trentishoe-covent-garden",
    name: "The Langford — Covent Garden 2BR",
    short_name: "The Langford",
    area: "Covent Garden",
    short_description:
      "A serene two-bedroom apartment within a classic mansion block — minutes from the Tube, moments from Covent Garden.",
    description:
      "Housed within a handsome period mansion block, this two-bedroom apartment offers a calm sanctuary at the centre of one of London's most coveted neighbourhoods. Two beautifully dressed bedrooms and a roomy double sofa bed welcome up to six guests, while the bright open-plan lounge is anchored by a 55-inch Smart TV with Netflix and PS4. A walk-in shower bathroom, fully equipped kitchen, and Nespresso coffee machine bring quiet daily luxury. Double glazing keeps the city's hum at bay, and a discreet lift carries you to your floor. Step outside and Covent Garden, Seven Dials, and the West End are all within minutes.",
    images: [
      "/apartments/image22.jpeg",
      "/apartments/image23.jpeg",
      "/apartments/image24.jpeg",
    ],
    features: {
      bedrooms: 2,
      bathrooms: 1,
      guests: 6,
      wifi: true,
      smart_tv: true,
      ac: false,
      kitchen: true,
      washing_machine: false,
      coffee_machine: true,
      elevator: true,
    },
    nearby_attractions: [
      "Covent Garden Piazza",
      "Seven Dials",
      "Royal Opera House",
      "Neal's Yard",
      "West End Theatres",
    ],
    transport: "Covent Garden Tube — a short walk",
    coordinates: { lat: 51.5142, lng: -0.124 },
  },
];

export const ADDRESS_DISCLOSURE_NOTE =
  "Exact address shared under written mandate.";

export const getApartmentById = (id) => apartments.find((a) => a.id === id);
